// ============================================================
// PerformanceOverlay.js - Debug stats display (F3 key toggle)
// Shows FPS, renderer info, entity count, chunk count, memory
// ============================================================

export class PerformanceOverlay {
  constructor() {
    this._visible = false;
    this._fps = 0;
    this._frameCount = 0;
    this._fpsTime = 0;
    this._frameTimes = [];
    this._maxFrameTimes = 60;

    this._build();
  }

  _build() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'perf-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 8px; left: 8px;
      z-index: 950;
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #0f0;
      background: rgba(0, 0, 0, 0.7);
      padding: 8px 12px;
      border-radius: 4px;
      pointer-events: none;
      display: none;
      line-height: 1.6;
      min-width: 240px;
      text-shadow: 1px 1px 0 #000;
      image-rendering: pixelated;
    `;
    document.body.appendChild(this.overlay);
  }

  toggle() {
    this._visible = !this._visible;
    this.overlay.style.display = this._visible ? 'block' : 'none';
  }

  get visible() { return this._visible; }

  /**
   * Update the overlay each frame.
   * @param {number} dt - delta time in seconds
   * @param {Object} stats - game state
   * @param {Object} stats.renderer - Three.js renderer
   * @param {number} stats.entityCount - passive entities
   * @param {number} stats.hostileCount - hostile mobs
   * @param {number} stats.chunkCount - loaded chunks
   * @param {{ x: number, y: number, z: number }} stats.playerPos
   * @param {string} stats.dimension
   * @param {boolean} stats.usingWebGPU
   */
  update(dt, stats) {
    if (!this._visible) return;

    // FPS calculation
    this._frameCount++;
    this._fpsTime += dt;
    this._frameTimes.push(dt);
    if (this._frameTimes.length > this._maxFrameTimes) this._frameTimes.shift();

    if (this._fpsTime >= 0.5) {
      this._fps = Math.round(this._frameCount / this._fpsTime);
      this._frameCount = 0;
      this._fpsTime = 0;
    }

    // Frame time stats
    const avgDt = this._frameTimes.reduce((a, b) => a + b, 0) / this._frameTimes.length;
    const maxDt = Math.max(...this._frameTimes);
    const minFps = Math.round(1 / maxDt);

    // Renderer info
    const info = stats.renderer?.info;
    const drawCalls = info?.render?.calls ?? '?';
    const triangles = info?.render?.triangles ?? '?';
    const geometries = info?.memory?.geometries ?? '?';
    const textures = info?.memory?.textures ?? '?';

    // Memory (if available)
    const mem = performance?.memory;
    const usedMB = mem ? (mem.usedJSHeapSize / 1048576).toFixed(1) : '?';
    const totalMB = mem ? (mem.totalJSHeapSize / 1048576).toFixed(1) : '?';

    // Position
    const px = stats.playerPos?.x?.toFixed(1) ?? '?';
    const py = stats.playerPos?.y?.toFixed(1) ?? '?';
    const pz = stats.playerPos?.z?.toFixed(1) ?? '?';

    // FPS color
    const fpsColor = this._fps >= 55 ? '#0f0' : this._fps >= 30 ? '#ff0' : '#f00';

    this.overlay.innerHTML = `
      <span style="color:${fpsColor};font-size:14px;font-weight:bold">${this._fps} FPS</span>
      <span style="color:#888;font-size:10px">(min ${minFps}, ${(avgDt * 1000).toFixed(1)}ms avg)</span><br>
      <span style="color:#aaa">Renderer:</span> ${stats.usingWebGPU ? '<span style="color:#4af">WebGPU</span>' : '<span style="color:#fa4">WebGL</span>'}<br>
      <span style="color:#aaa">Draw calls:</span> ${drawCalls} | <span style="color:#aaa">Tris:</span> ${typeof triangles === 'number' ? (triangles/1000).toFixed(1) + 'K' : triangles}<br>
      <span style="color:#aaa">Geometries:</span> ${geometries} | <span style="color:#aaa">Textures:</span> ${textures}<br>
      <span style="color:#aaa">Chunks:</span> ${stats.chunkCount ?? '?'} | <span style="color:#aaa">Entities:</span> ${stats.entityCount ?? 0}+${stats.hostileCount ?? 0}<br>
      <span style="color:#aaa">Pos:</span> ${px}, ${py}, ${pz} | <span style="color:#aaa">Dim:</span> ${stats.dimension ?? 'overworld'}<br>
      <span style="color:#aaa">Heap:</span> ${usedMB}/${totalMB} MB
    `;

    // Reset renderer info counters for next frame
    if (info?.reset) info.reset();
  }
}
